import { useState } from 'react'
import { Building2, Package, Clock, Users, TrendingUp, Calendar, Euro } from 'lucide-react'
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { reports as reportsApi, projects as projectsApi } from '@/utils/api'
import { getMonthName } from '@/utils/formatters'
import { cn } from '@/lib/utils'

interface ProjectSummary {
  totalOrders: number
  totalOrderValue: number
  totalHours: number
  uniqueWorkers: number
  totalWorkDays: number
  avgHoursPerDay: number
}

interface ProjectStats {
  summary: ProjectSummary
  charts: {
    monthlyOrders: { month: number; count: number; value: number }[]
    workerHours: { name: string; hours: number }[]
  }
  orders: { id: number; created_at: string; user_name: string; status: string; items: { quantity?: number; price?: number; einzelpreis?: number }[] }[]
  workers: { name: string; days: number; hours: number; avgHoursPerDay: number }[]
  materials: { name: string; quantity: number; value: number }[]
  timeline: { date: string; hours: number }[]
}

const fmtCurrency = (v: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0)
const fmtHours = (h: number) => `${h.toFixed(1)} Std.`

const tabDefs = [
  { id: 'overview', label: 'Übersicht', icon: TrendingUp },
  { id: 'orders', label: 'Bestellungen', icon: Package },
  { id: 'workers', label: 'Mitarbeiter', icon: Users },
  { id: 'materials', label: 'Materialien', icon: Building2 },
  { id: 'timeline', label: 'Zeitverlauf', icon: Calendar },
] as const

type TabId = (typeof tabDefs)[number]['id']

export default function BauvorhabenStatistiken() {
  const [selectedProject, setSelectedProject] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [dateRange, setDateRange] = useState({
    from: `${new Date().getFullYear()}-01-01`,
    to: new Date().toISOString().split('T')[0],
  })

  const { data: projects = [], isLoading: loadingProjects } = useQuery<{ id: number; name: string }[]>({
    queryKey: ['projects-active'],
    queryFn: () => projectsApi.getAll({ status: 'active' }).then(r => r.data || []),
  })

  const { data: stats, isLoading: loading } = useQuery<ProjectStats>({
    queryKey: ['project-stats', selectedProject, dateRange],
    queryFn: () => reportsApi.projectStats(selectedProject!, dateRange).then(r => r.data),
    enabled: !!selectedProject,
  })

  if (loadingProjects) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full" /></div>

  return (
    <div>
      <PageHeader title="Bauvorhaben-Statistiken" description="Detaillierte Auswertungen pro Bauvorhaben" />

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium">Bauvorhaben auswählen</label>
          <select
            value={selectedProject || ''}
            onChange={(e) => setSelectedProject(e.target.value ? parseInt(e.target.value) : null)}
            className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm"
          >
            <option value="">Bauvorhaben auswählen...</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Von</label>
          <Input type="date" value={dateRange.from} onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Bis</label>
          <Input type="date" value={dateRange.to} onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })} />
        </div>
      </div>

      {!selectedProject ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Building2 className="mx-auto mb-4 h-12 w-12 opacity-40" />
          <p>Bitte wählen Sie ein Bauvorhaben aus.</p>
        </CardContent></Card>
      ) : loading ? (
        <div className="space-y-4"><Skeleton className="h-32 w-full" /><Skeleton className="h-64 w-full" /></div>
      ) : !stats ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Keine Daten verfügbar.</CardContent></Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              { icon: Package, color: 'bg-blue-100 text-blue-600', label: 'Bestellungen', value: stats.summary.totalOrders },
              { icon: Euro, color: 'bg-green-100 text-green-600', label: 'Bestellwert', value: fmtCurrency(stats.summary.totalOrderValue) },
              { icon: Clock, color: 'bg-orange-100 text-orange-600', label: 'Arbeitsstunden', value: fmtHours(stats.summary.totalHours) },
              { icon: Users, color: 'bg-purple-100 text-purple-600', label: 'Mitarbeiter', value: stats.summary.uniqueWorkers },
            ].map((s, i) => (
              <Card key={i}>
                <CardContent className="py-4">
                  <div className="flex items-center">
                    <div className={cn('mr-3 rounded-lg p-2', s.color)}><s.icon className="h-5 w-5" /></div>
                    <div>
                      <p className="text-sm text-muted-foreground">{s.label}</p>
                      <p className="text-xl font-semibold">{s.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b">
            <nav className="flex space-x-4">
              {tabDefs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                  )}
                >
                  <tab.icon className="mr-2 h-4 w-4" />{tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Overview */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader><CardTitle>Bestellungen pro Monat</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.charts.monthlyOrders.map(m => ({ name: getMonthName(m.month, true), bestellungen: m.count, wert: m.value }))}>
                        <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" />
                        <YAxis yAxisId="left" /><YAxis yAxisId="right" orientation="right" />
                        <Tooltip formatter={(value: number, name: string) => [name === 'wert' ? fmtCurrency(value) : value, name === 'bestellungen' ? 'Bestellungen' : 'Wert']} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="bestellungen" fill="#1E4B8E" radius={[4,4,0,0]} />
                        <Bar yAxisId="right" dataKey="wert" fill="#10B981" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Stunden pro Mitarbeiter</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.charts.workerHours} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis type="category" dataKey="name" width={80} />
                        <Tooltip formatter={(value: number) => [fmtHours(value), 'Stunden']} />
                        <Bar dataKey="hours" fill="#F7941D" radius={[0,4,4,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle>Zusammenfassung</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    {[
                      { val: stats.summary.totalWorkDays, label: 'Arbeitstage' },
                      { val: fmtHours(stats.summary.avgHoursPerDay), label: 'Stunden/Tag' },
                      { val: stats.materials.length, label: 'Verschiedene Materialien' },
                      { val: stats.summary.totalOrders > 0 ? fmtCurrency(stats.summary.totalOrderValue / stats.summary.totalOrders) : '-', label: 'Bestellung' },
                    ].map((s, i) => (
                      <div key={i} className="rounded-lg bg-muted p-4 text-center">
                        <p className="text-2xl font-bold text-primary-600">{s.val}</p>
                        <p className="text-sm text-muted-foreground">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Orders */}
          {activeTab === 'orders' && (
            <Card>
              <CardHeader><CardTitle>Bestellungen ({stats.orders.length})</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/50">
                      {['Datum','Mitarbeiter','Status','Artikel','Wert'].map(h => (
                        <th key={h} className={cn("px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground", h === 'Wert' ? 'text-right' : 'text-left')}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {stats.orders.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Keine Bestellungen</td></tr>
                      ) : stats.orders.map((o) => {
                        const val = o.items.reduce((s, i) => s + (i.quantity || 1) * (i.price || i.einzelpreis || 0), 0)
                        return (
                          <tr key={o.id} className="border-b hover:bg-muted/50">
                            <td className="px-4 py-3 text-sm">{o.created_at ? new Date(o.created_at).toLocaleDateString('de-DE') : '-'}</td>
                            <td className="px-4 py-3 text-sm">{o.user_name}</td>
                            <td className="px-4 py-3"><Badge variant={o.status === 'completed' ? 'success' : o.status === 'submitted' ? 'warning' : 'secondary'}>{o.status === 'completed' ? 'Abgeschlossen' : o.status === 'submitted' ? 'Eingereicht' : o.status}</Badge></td>
                            <td className="px-4 py-3 text-sm">{o.items.length} Artikel</td>
                            <td className="px-4 py-3 text-right text-sm font-medium">{fmtCurrency(val)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Workers */}
          {activeTab === 'workers' && (
            <Card>
              <CardHeader><CardTitle>Mitarbeiter-Auswertung</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">Name</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-muted-foreground">Arbeitstage</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-muted-foreground">Gesamtstunden</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-muted-foreground">Stunden/Tag</th>
                    </tr></thead>
                    <tbody>
                      {stats.workers.length === 0 ? (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Keine Daten</td></tr>
                      ) : stats.workers.map((w, i) => (
                        <tr key={i} className="border-b hover:bg-muted/50">
                          <td className="px-4 py-3 text-sm font-medium">{w.name}</td>
                          <td className="px-4 py-3 text-right text-sm">{w.days}</td>
                          <td className="px-4 py-3 text-right text-sm font-medium">{fmtHours(w.hours)}</td>
                          <td className="px-4 py-3 text-right text-sm">{fmtHours(w.avgHoursPerDay)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {stats.workers.length > 0 && (
                      <tfoot className="bg-muted">
                        <tr>
                          <td className="px-4 py-3 text-sm font-bold">Gesamt</td>
                          <td className="px-4 py-3 text-right text-sm font-bold">{stats.summary.totalWorkDays}</td>
                          <td className="px-4 py-3 text-right text-sm font-bold">{fmtHours(stats.summary.totalHours)}</td>
                          <td className="px-4 py-3 text-right text-sm font-bold">{fmtHours(stats.summary.avgHoursPerDay)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Materials */}
          {activeTab === 'materials' && (
            <Card>
              <CardHeader><CardTitle>Materialverbrauch</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">Material</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-muted-foreground">Menge</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-muted-foreground">Gesamtwert</th>
                    </tr></thead>
                    <tbody>
                      {stats.materials.length === 0 ? (
                        <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">Keine Materialien</td></tr>
                      ) : stats.materials.map((m, i) => (
                        <tr key={i} className="border-b hover:bg-muted/50">
                          <td className="px-4 py-3 text-sm">{m.name}</td>
                          <td className="px-4 py-3 text-right text-sm">{m.quantity}</td>
                          <td className="px-4 py-3 text-right text-sm font-medium">{fmtCurrency(m.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {stats.materials.length > 0 && (
                      <tfoot className="bg-muted">
                        <tr>
                          <td className="px-4 py-3 text-sm font-bold">Gesamt</td>
                          <td className="px-4 py-3 text-right text-sm font-bold">{stats.materials.reduce((s, m) => s + m.quantity, 0)}</td>
                          <td className="px-4 py-3 text-right text-sm font-bold">{fmtCurrency(stats.summary.totalOrderValue)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          {activeTab === 'timeline' && (
            <Card>
              <CardHeader><CardTitle>Arbeitsstunden im Zeitverlauf</CardTitle></CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.timeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                      <YAxis />
                      <Tooltip formatter={(value: number) => [fmtHours(value), 'Stunden']} labelFormatter={(l: string) => `Datum: ${l}`} />
                      <Line type="monotone" dataKey="hours" stroke="#1E4B8E" strokeWidth={2} dot={{ fill: '#1E4B8E', r: 3 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
